/**
 * Funnel firsts — каждый milestone эмитится `funnel.first` ровно один
 * раз за жизнь пользователя. JB 2026-05-26: «ux ui флоу аналитику надо
 * тоже» — funnel firsts отвечают на вопрос «сколько времени игрок
 * тратит между ключевыми моментами»: первый тап, первая фраза, первая
 * готовая кристаллизация, первая покупка апгрейда и т.д.
 *
 * Persistence: `sai-funnel-firsts-v1` в localStorage. Сохранение нужно
 * чтобы возврат в игру не reissue'ил «первый тап» каждый раз. Без
 * persist это была бы метрика «первого тапа в сессии», а не «первого
 * тапа в жизни игрока».
 *
 * Сессия sessionStartedAt — runtime, не persisted. Время до milestone
 * считается от начала ТЕКУЩЕЙ сессии (даже если milestone был достигнут
 * в эту сессию — это самый информативный таймер для funnel analysis).
 *
 * Использование:
 *   const seen = useFunnelStore.getState().seen;
 *   if (!seen.has('first_tap')) {
 *     useFunnelStore.getState().markSeen('first_tap');
 *   }
 *
 * Сам emit делает store при markSeen — компоненты только декларируют
 * «вот ещё один milestone достигнут».
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { emit } from './emit.js';
import type { EventPayloads } from './events.js';

export type FunnelMilestone = EventPayloads['funnel.first']['milestone'];

interface FunnelStore {
  /** Set seen milestones — JSON serialized as Array через replacer. */
  readonly seen: ReadonlySet<FunnelMilestone>;
  /** Когда стартовала текущая сессия (runtime, не persisted). */
  readonly sessionStartedAt: number;
  /** Запись milestone'а. Идемпотентна — повторные вызовы no-op. */
  markSeen(milestone: FunnelMilestone): void;
  /** Test-only reset. */
  _reset(): void;
}

export const useFunnelStore = create<FunnelStore>()(
  persist(
    (set, get) => ({
      seen: new Set<FunnelMilestone>(),
      sessionStartedAt: Date.now(),
      markSeen(milestone) {
        const { seen, sessionStartedAt } = get();
        if (seen.has(milestone)) return;
        const next = new Set(seen);
        next.add(milestone);
        set({ seen: next });
        emit('funnel.first', {
          milestone,
          time_since_session_start_ms: Date.now() - sessionStartedAt,
        });
      },
      _reset() {
        set({ seen: new Set(), sessionStartedAt: Date.now() });
      },
    }),
    {
      name: 'sai-funnel-firsts-v1',
      // Set нельзя сериализовать в JSON напрямую — конвертируем в Array
      // на write, и обратно на read через partialize+merge.
      partialize: (s) => ({ seen: Array.from(s.seen) }),
      merge: (persisted, current) => {
        const arr =
          persisted !== null && typeof persisted === 'object' && 'seen' in persisted
            ? (persisted.seen as readonly FunnelMilestone[])
            : [];
        return {
          ...current,
          seen: new Set(arr),
        };
      },
    },
  ),
);

/** Convenience helper — короткая форма для UI кода. */
export function markFunnel(milestone: FunnelMilestone): void {
  useFunnelStore.getState().markSeen(milestone);
}
